// Servicio para llamar al backend local NotebookLM (generación audiovisual admin).
// Solo tú (admin) usas este flujo: el backend corre en tu PC con tu cuenta de Google.
const API_URL = (import.meta.env.VITE_NOTEBOOKLM_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const ADMIN_TOKEN = import.meta.env.VITE_NOTEBOOKLM_ADMIN_TOKEN || '';

const request = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': ADMIN_TOKEN,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch (_) { /* noop */ }
    throw new Error(detail);
  }
  return res.json();
};

// Comienza la generación de un tema. Devuelve { task_id }.
export const startGeneration = (topic, artifacts = ['audio', 'infographic', 'slides']) =>
  request('/api/generate', {
    method: 'POST',
    body: JSON.stringify({ topic, artifacts }),
  });

// Consulta el estado de un trabajo (polling).
export const getTaskStatus = (taskId) => request(`/api/generate/${taskId}`);

// Estado y cuota del backend (útil para mostrar si se puede generar).
export const getGeneratorHealth = () => request('/health');

// Desbloquea manualmente la cuota/cooldown de la cuenta activa (solo admin).
export const resetGeneratorQuota = () => request('/api/rate/reset', { method: 'POST' });

// ── Sesión de NotebookLM (cuenta de Google del admin) ──

// Cuenta de Google activa. Devuelve { authenticated, account }.
export const getNotebooklmStatus = () => request('/api/notebooklm/status');

// Abre en tu PC la ventana de Google para iniciar sesión o cambiar de cuenta.
export const notebooklmLogin = () => request('/api/notebooklm/login', { method: 'POST' });

// Cierra la sesión actual de NotebookLM (para entrar con otra cuenta).
export const notebooklmLogout = () => request('/api/notebooklm/logout', { method: 'POST' });

// Últimas líneas de log del backend (consola + buffer en memoria).
// Devuelve { logs: [{ ts, level, msg }] }.
export const getNotebooklmLogs = (lines = 200) =>
  request(`/api/notebooklm/logs?lines=${lines}`);

// Convierte un tema del Lector (objeto Firestore) en el payload que espera el backend.
export const topicToPayload = (tema) => ({
  id: tema.id,
  title: tema.title,
  module: tema.module || '',
  lessons: Array.isArray(tema.lessons) ? tema.lessons : [],
});

// Construye el payload para generar UN set audiovisual de una unidad:
//   scope "topic"  → todo el tema (lecciones completas)
//   scope "module" → solo las lecciones de ese módulo (lesson.module === unit)
//   scope "lesson" → solo esa lección (lesson.id === unit)
// unit_key identifica la unidad de forma única (tema/módulo/lección) para que
// el backend pueda reanudar tareas interrumpidas sin regenerar lo ya hecho.
const unitSlug = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unidad';

export const buildUnitPayload = (tema, { scope = 'topic', unit = '', area = '' } = {}) => {
  const all = Array.isArray(tema.lessons) ? tema.lessons : [];
  const temaId = tema.id || (tema.title || 'tema').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  if (scope === 'module') {
    const lessons = all.filter((l) => (l.module || '').trim() === unit);
    return {
      id: tema.id,
      title: `${tema.title} · ${unit}`,
      module: unit,
      unit_key: `module:${temaId}:${(unit || '').trim()}`,
      area,
      unit: unitSlug(unit),
      lessons,
    };
  }
  if (scope === 'lesson') {
    const lesson = all.find((l) => l.id === unit) || all.find((l) => (l.title || '') === unit);
    const lessons = lesson ? [lesson] : [];
    return {
      id: tema.id,
      title: lesson ? `${tema.title} · ${lesson.title}` : tema.title,
      module: (lesson && lesson.module) || tema.module || '',
      unit_key: `lesson:${temaId}:${(lesson && lesson.id) || 'sin-id'}`,
      area,
      unit: lesson ? unitSlug(lesson.title) : unitSlug(unit),
      lessons,
    };
  }
  return {
    id: tema.id,
    title: tema.title,
    module: tema.module || '',
    unit_key: `topic:${temaId}`,
    area,
    unit: '',
    lessons: all,
  };
};

// Lista de unidades disponibles de un tema para el selector del admin:
//   modules → [{ name, count }] (lecciones agrupadas por lesson.module)
//   lessons → [{ id, title, module }]
export const getTemaUnits = (tema) => {
  const all = Array.isArray(tema.lessons) ? tema.lessons : [];
  const modulesMap = {};
  all.forEach((l) => {
    const m = (l.module || '').trim() || 'General';
    modulesMap[m] = (modulesMap[m] || 0) + 1;
  });
  return {
    modules: Object.entries(modulesMap).map(([name, count]) => ({ name, count })),
    lessons: all.map((l) => ({
      id: l.id,
      title: l.title || 'Sin título',
      module: (l.module || '').trim() || 'General',
    })),
  };
};